import { Component, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ConciergeService } from './services/concierge.service';
import { ChatMessage } from './shared/chat-overlay/chat-overlay.component';
import { CanonicalService } from './shared/services/canonical.service';
import { StructuredDataService } from './shared/services/structured-data.service';

const INACTIVITY_WARN_MS  = 4 * 60 * 1000; // aviso a los 4 min
const INACTIVITY_CLOSE_MS = 5 * 60 * 1000; // cierre a los 5 min

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  chatOpen = false;
  sending  = false;
  typing   = false;

  msgs: ChatMessage[] = [
    {
      role: 'assistant',
      text: 'Hola 👋 Soy el concierge de **Signature Tours**. Puedo ayudarte a encontrar tours, consultar disponibilidad y hacer tu reserva. ¿Qué estás buscando?',
      chips: ['Tours en Buenos Aires', 'Cataratas del Iguazú', 'Estadio Boca', 'Hablar con un agente'],
    },
  ];

  private warnTimer:  ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private concierge: ConciergeService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private canonicalService: CanonicalService,
    private structuredData: StructuredDataService
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.viewportScroller.scrollToPosition([0, 0]);
        const url = event?.urlAfterRedirects || this.router.url;
        this.canonicalService.updateRouteLinks(url);
        this.setBreadcrumbJsonLd(url);
      });

    this.canonicalService.updateRouteLinks(this.router.url);
    this.setBreadcrumbJsonLd(this.router.url);
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  // ── Chat open / close ──────────────────────────────────────────
  toggleChat(): void {
    this.chatOpen = !this.chatOpen;
    if (this.chatOpen) this.resetInactivityTimer();
    else this.clearTimers();
  }

  closeChat(): void {
    this.chatOpen = false;
    this.clearTimers();
  }

  // ── Inactivity timer ──────────────────────────────────────────
  private resetInactivityTimer(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.clearTimers();
    if (!this.chatOpen) return;

    this.warnTimer = setTimeout(() => {
      this.msgs.push({
        role: 'assistant',
        text: '¿Seguís ahí? El chat se cerrará automáticamente en 1 minuto por inactividad.',
        chips: ['Sigo aquí', 'Cerrar chat'],
      });
    }, INACTIVITY_WARN_MS);

    this.closeTimer = setTimeout(() => {
      this.msgs.push({ role: 'assistant', text: 'El chat se cerró por inactividad. ¡Hasta pronto! 👋' });
      this.chatOpen = false;
    }, INACTIVITY_CLOSE_MS);
  }

  private clearTimers(): void {
    if (this.warnTimer)  { clearTimeout(this.warnTimer);  this.warnTimer  = null; }
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
  }

  // ── Message handling ──────────────────────────────────────────
  async onSend(text: string) {
    // Chips de control — no van al bot
    if (text === 'WhatsApp') { window.open('https://wa.me/5491148575956', '_blank'); return; }
    if (text === 'Enviar email') { window.open('mailto:info@signaturetours.com.ar', '_blank'); return; }
    if (text === 'Sigo aquí') {
      this.msgs.push({ role: 'user', text });
      this.msgs.push({ role: 'assistant', text: '¡Perfecto! Seguimos. ¿En qué te puedo ayudar?', chips: ['Ver tours', 'Consultar disponibilidad', 'Hablar con un agente'] });
      this.resetInactivityTimer();
      return;
    }
    if (text === 'Cerrar chat') { this.closeChat(); return; }

    // Resetea inactividad en cada mensaje del usuario
    this.resetInactivityTimer();

    this.msgs.push({ role: 'user', text });

    const conversation = this.msgs.map(m => ({ role: m.role, content: m.text }));

    this.sending = true;
    this.typing  = true;
    try {
      const res = await this.concierge.send(text, conversation).toPromise();
      const botText = (res?.text || res?.reply || 'Listo. ¿Seguimos?').toString();

      const isEscalation = botText.includes('wa.me') || botText.includes('info@signaturetours');
      let chips: string[] | undefined = res?.chips?.length ? res.chips : undefined;
      if (isEscalation) chips = ['WhatsApp', 'Enviar email'];

      this.msgs.push({ role: 'assistant', text: botText, chips });
    } catch (err) {
      console.error(err);
      this.msgs.push({ role: 'assistant', text: 'Uy, hubo un problema. Probá de nuevo en unos segundos.' });
    } finally {
      this.sending = false;
      this.typing  = false;
    }
  }

  // ── SEO ──────────────────────────────────────────────────────
  private setBreadcrumbJsonLd(url: string): void {
    const cleanPath = (url || '/').split('?')[0].split('#')[0];
    const rawSegments = cleanPath.split('/').filter(Boolean);
    const supportedLangs = new Set(['en', 'es', 'pt', 'zh-cn']);
    let lang = 'en';
    if (rawSegments.length > 0 && supportedLangs.has(rawSegments[rawSegments.length - 1].toLowerCase())) {
      lang = rawSegments[rawSegments.length - 1].toLowerCase();
    }
    const segments = rawSegments.filter((seg, idx) => {
      const isLast = idx === rawSegments.length - 1;
      return !(isLast && supportedLangs.has(seg.toLowerCase()));
    });

    const items: Array<{ '@type': 'ListItem'; position: number; name: string; item: string }> = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: this.canonicalService.buildAbsolute(CanonicalService.homeAlias(`/home/${lang}`)) },
    ];

    let path = '';
    let position = 2;
    segments.forEach((seg) => {
      path += `/${seg}`;
      if (/^\d+$/.test(seg)) return;
      // Segmentos intermedios que no son una página propia (ej. /destino/en no existe).
      if (seg === 'destino' || seg === 'home') return;
      const pathWithLang = path ? `${path}/${lang}` : `/home/${lang}`;
      items.push({ '@type': 'ListItem', position: position++, name: this.segmentToTitle(seg), item: this.canonicalService.buildAbsolute(pathWithLang) });
    });

    this.structuredData.setJsonLd('breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    });
  }

  private segmentToTitle(segment: string): string {
    return decodeURIComponent(segment || '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }
}
